import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { billingAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Check } from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState({});
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState({});
  const { user, loadUser } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    billingAPI
      .plans()
      .then((r) => setPlans(r.data.plans || {}))
      .catch(() => {});
    billingAPI
      .subscription()
      .then((r) => setSub(r.data))
      .catch(() => {});
  }, []);

  // Handle return from Dodo checkout
  useEffect(() => {
    const payment = searchParams.get("payment");
    if (payment === "success") {
      toast.success("Subscription activated! Welcome aboard 🎉");
      // Reload user data after short delay to give webhook time to fire
      setTimeout(() => loadUser(), 3000);
    }
  }, [searchParams, loadUser]);

  const dodoCheckout = async (planKey) => {
    setLoading((l) => ({ ...l, [planKey]: true }));
    try {
      const { data } = await billingAPI.dodoCreateSession({ plan: planKey });
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to start checkout");
      setLoading((l) => ({ ...l, [planKey]: false }));
    }
  };

  const cancel = async () => {
    if (
      !window.confirm(
        "Cancel your subscription? You'll retain access until end of billing period."
      )
    )
      return;
    try {
      await billingAPI.cancel();
      toast.success("Subscription cancelled");
      setSub((s) => ({ ...s, planStatus: "cancelled" }));
    } catch {
      toast.error("Cancellation failed");
    }
  };

  const formatLimit = (val) => {
    if (val === null || val === undefined) return "Unlimited";
    return val.toLocaleString();
  };

  const planOrder = ["starter", "business", "premium"];
  const popular = "business";

  return (
    <div>
      <div
        className="page-header"
        style={{ textAlign: "center", marginBottom: 40 }}
      >
        <h1 style={{ fontSize: 32 }}>Simple, transparent pricing</h1>
        <p>Start free, upgrade when you're ready. No hidden fees.</p>
      </div>

      {/* Current plan banner */}
      {user?.plan && user.plan !== "trial" && (
        <div
          className="card"
          style={{
            marginBottom: 28,
            background: "rgba(26,158,92,0.06)",
            border: "1px solid rgba(26,158,92,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              Current plan:{" "}
              <span
                style={{ color: "var(--brand)", textTransform: "capitalize" }}
              >
                {user.plan}
              </span>
              {sub?.planStatus === "cancelled" && (
                <span className="badge badge-amber" style={{ marginLeft: 8 }}>
                  Cancels at period end
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: "var(--text2)" }}>
              {sub?.usage?.botsCreated || 0} /{" "}
              {formatLimit(plans[user.plan]?.maxBots)} bots used
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {sub?.planStatus !== "cancelled" && (
              <button className="btn btn-danger btn-sm" onClick={cancel}>
                Cancel plan
              </button>
            )}
          </div>
        </div>
      )}

      <div className="pricing-grid">
        {planOrder.map((key) => {
          const plan = plans[key];
          if (!plan) return null;
          const isCurrent = user?.plan === key;
          const isPopular = key === popular;

          return (
            <div
              key={key}
              className={`pricing-card${isPopular ? " popular" : ""}`}
            >
              {isPopular && (
                <div
                  style={{
                    background: "var(--brand)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    padding: "3px 12px",
                    borderRadius: 20,
                    alignSelf: "flex-start",
                  }}
                >
                  Most popular
                </div>
              )}
              <div>
                <div
                  style={{
                    fontFamily: "var(--font-head)",
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 4,
                    textTransform: "capitalize",
                  }}
                >
                  {plan.name}
                </div>
                <div className="pricing-price">
                  ${plan.price}
                  <span>/mo</span>
                </div>
              </div>

              <ul className="pricing-features">
                {[
                  `${formatLimit(plan.maxBots)} chatbot${plan.maxBots !== 1 ? "s" : ""}`,
                  `${formatLimit(plan.maxConversationsPerMonth)} messages/month`,
                  `${formatLimit(plan.maxKnowledgeEntries)} knowledge entries per bot`,
                  plan.features?.leadExport && "Lead capture & CRM export",
                  plan.features?.analytics && "Analytics dashboard",
                  plan.features?.removeBranding && "Remove Wispy branding",
                  plan.features?.apiAccess && "API access",
                  plan.features?.prioritySupport && "Priority support",
                ]
                  .filter(Boolean)
                  .map((feat, i) => (
                    <li key={i}>
                      <Check
                        size={14}
                        style={{ color: "var(--brand)", flexShrink: 0 }}
                      />
                      {feat}
                    </li>
                  ))}
              </ul>

              {isCurrent ? (
                <button
                  className="btn btn-secondary"
                  disabled
                  style={{ width: "100%" }}
                >
                  ✓ Current plan
                </button>
              ) : (
                <button
                  className={`btn ${isPopular ? "btn-primary" : "btn-secondary"}`}
                  style={{ width: "100%" }}
                  disabled={!!loading[key]}
                  onClick={() => dodoCheckout(key)}
                >
                  {loading[key] ? "Redirecting…" : "Get started →"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p
        style={{
          textAlign: "center",
          fontSize: 13,
          color: "var(--text3)",
          marginTop: 32,
        }}
      >
        All plans include a 14-day free trial. Cancel anytime. Prices in USD.
      </p>
    </div>
  );
}
